import React, { useEffect, useRef, useState, useCallback, memo } from 'react';

const InteractiveBackground = memo(() => {
  const canvasRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(true);
  const animationRef = useRef(null);
  const lastMousePosRef = useRef({ x: 0, y: 0 });

  // Throttled mouse move handler
  const handleMouseMove = useCallback((e) => {
    if (animationRef.current) {
      lastMousePosRef.current = { x: e.clientX, y: e.clientY };
      setMousePos(lastMousePosRef.current);
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationId;
    let lastMousePos = { x: 0, y: 0 };

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Throttled mouse move handler
    let mouseMoveTimeout;
    const throttledMouseMove = (e) => {
      if (mouseMoveTimeout) return;
      mouseMoveTimeout = setTimeout(() => {
        lastMousePos = { x: e.clientX, y: e.clientY };
        setMousePos(lastMousePos);
        mouseMoveTimeout = null;
      }, 16); // ~60fps throttling
    };

    window.addEventListener('mousemove', throttledMouseMove);

    // Animation variables
    let time = 0;
    const particles = [];

    // Reduced particle count for better performance
    for (let i = 0; i < 15; i++) { // Reduced from 25 to 15
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.2, // Reduced velocity
        vy: (Math.random() - 0.5) * 0.2,
        size: Math.random() * 2 + 1, // Reduced size
        opacity: Math.random() * 0.06 + 0.03 // Reduced opacity
      });
    }

    // Optimized animation loop
    const animate = () => {
      time += 0.006; // Slower time increment
      
      // Clear canvas with optimized gradient
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, 'rgba(245, 247, 255, 0.98)');
      gradient.addColorStop(1, 'rgba(235, 240, 255, 0.98)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Update and draw particles with reduced complexity
      particles.forEach((particle, index) => {
        // Update position
        particle.x += particle.vx;
        particle.y += particle.vy;

        // Bounce off edges
        if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;

        // Simplified mouse interaction
        const dx = lastMousePos.x - particle.x;
        const dy = lastMousePos.y - particle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 150) { // Reduced interaction radius
          const force = (150 - distance) / 150;
          particle.x -= dx * force * 0.01; // Reduced force
          particle.y -= dy * force * 0.01;
        }

        // Draw particle
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(120, 144, 180, ${particle.opacity})`;
        ctx.fill();

        // Simplified connections - only draw to nearby particles
        particles.forEach((otherParticle, otherIndex) => {
          if (index !== otherIndex && index < otherIndex) {
            const dx = particle.x - otherParticle.x;
            const dy = particle.y - otherParticle.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            if (distance < 80) { // Reduced connection distance
              ctx.beginPath();
              ctx.moveTo(particle.x, particle.y);
              ctx.lineTo(otherParticle.x, otherParticle.y);
              ctx.strokeStyle = `rgba(120, 144, 180, ${0.015 * (1 - distance / 80)})`;
              ctx.lineWidth = 0.5; // Reduced line width
              ctx.stroke();
            }
          }
        });
      });

      // Simplified geometric shapes
      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(time * 0.02); // Slower rotation

      // Single rotating hexagon
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const angle = (i * Math.PI) / 3;
        const x = Math.cos(angle) * 80; // Reduced size
        const y = Math.sin(angle) * 80;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.strokeStyle = 'rgba(120, 144, 180, 0.015)';
      ctx.lineWidth = 1;
      ctx.stroke();

      ctx.restore();

      animationId = requestAnimationFrame(animate);
    };

    // Start animation
    animationRef.current = true;
    setTimeout(() => {
      animate();
    }, 200);

    // Cleanup
    return () => {
      animationRef.current = false;
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', throttledMouseMove);
      if (mouseMoveTimeout) clearTimeout(mouseMoveTimeout);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ 
        opacity: isVisible ? 1 : 0, 
        transition: 'opacity 0.5s ease-in-out',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%'
      }}
    />
  );
});

InteractiveBackground.displayName = 'InteractiveBackground';

export default InteractiveBackground; 